// ═══════════════════════════════════════════════════════════════════════════════
// FLOATING CONTACT WIDGET v1.0
// EnergiPro Solutions — Multi-Channel Communication Widget
// ═══════════════════════════════════════════════════════════════════════════════
// WhatsApp (primary) + Call (secondary) + Book Call (secondary)
// Mobile responsive, non-intrusive, clean design
// Priority-aware: displays channels based on RFQ priority context
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { MessageCircle, Phone, Calendar, X, ChevronUp } from 'lucide-react';
import {
  CONTACT_CONFIG,
  generateWhatsAppLink,
  generatePhoneLink,
  generateCalendarLink,
  trackContactInteraction,
  ContactChannel,
} from './multiChannelContactEngine';

interface FloatingContactWidgetProps {
  rfqId?: string;
  serviceName?: string;
  showPhone?: boolean;
  showCalendar?: boolean;
}

export const FloatingContactWidget: React.FC<FloatingContactWidgetProps> = ({
  rfqId,
  serviceName,
  showPhone = true,
  showCalendar = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [pulseActive, setPulseActive] = useState(true);

  // Stop pulse animation after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => setPulseActive(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleChannelClick = (channel: ContactChannel, url: string) => {
    if (rfqId) {
      trackContactInteraction(rfqId, channel, {
        source: 'floating_widget',
        serviceName: serviceName || '',
      });
    }
    setHasInteracted(true);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const whatsappUrl = generateWhatsAppLink(rfqId, serviceName);
  const phoneUrl = generatePhoneLink();
  const calendarUrl = generateCalendarLink(rfqId, serviceName);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Expanded Menu */}
      {isOpen && (
        <div className="flex flex-col gap-2 mb-2 animate-fade-in">
          {/* Calendar Booking */}
          {showCalendar && (
            <button
              onClick={() => handleChannelClick('calendar', calendarUrl)}
              className="group flex items-center gap-3 bg-white border border-slate-200 rounded-full pl-4 pr-5 py-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-blue-300 hover:-translate-y-0.5"
              aria-label="Schedule Technical Call"
            >
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <Calendar className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800 leading-tight">Schedule Call</p>
                <p className="text-xs text-slate-500">Book engineering discussion</p>
              </div>
            </button>
          )}

          {/* Direct Call */}
          {showPhone && (
            <a
              href={phoneUrl}
              onClick={() => {
                if (rfqId) {
                  trackContactInteraction(rfqId, 'phone', {
                    source: 'floating_widget',
                    serviceName: serviceName || '',
                  });
                }
                setHasInteracted(true);
              }}
              className="group flex items-center gap-3 bg-white border border-slate-200 rounded-full pl-4 pr-5 py-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-green-300 hover:-translate-y-0.5"
              aria-label="Call EnergiPro"
            >
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                <Phone className="w-4.5 h-4.5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800 leading-tight">Call Now</p>
                <p className="text-xs text-slate-500">{CONTACT_CONFIG.phone.display}</p>
              </div>
            </a>
          )}

          {/* WhatsApp */}
          <button
            onClick={() => handleChannelClick('whatsapp', whatsappUrl)}
            className="group flex items-center gap-3 bg-white border border-slate-200 rounded-full pl-4 pr-5 py-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-emerald-300 hover:-translate-y-0.5"
            aria-label="WhatsApp EnergiPro"
          >
            <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
              <MessageCircle className="w-4.5 h-4.5 text-emerald-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-800 leading-tight">WhatsApp</p>
              <p className="text-xs text-slate-500">Instant response</p>
            </div>
          </button>
        </div>
      )}

      {/* Main Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center
          transition-all duration-300 hover:scale-105 active:scale-95
          ${isOpen
            ? 'bg-slate-800 hover:bg-slate-700'
            : 'bg-emerald-500 hover:bg-emerald-600'
          }
        `}
        aria-label={isOpen ? 'Close contact menu' : 'Open contact menu'}
      >
        {/* Pulse Ring */}
        {!isOpen && pulseActive && (
          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-30" />
        )}

        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}

        {/* Interaction Badge */}
        {hasInteracted && !isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full border-2 border-white" />
        )}
      </button>
    </div>
  );
};

// ─── INLINE CONTACT BUTTONS (For Portal & Emails) ────────────────────────────

interface InlineContactButtonsProps {
  rfqId: string;
  serviceName?: string;
  priority: 'high' | 'medium' | 'low';
  compact?: boolean;
}

export const InlineContactButtons: React.FC<InlineContactButtonsProps> = ({
  rfqId,
  serviceName,
  priority,
  compact = false,
}) => {
  const showPhone = priority === 'high';
  const showCalendar = priority === 'high' || priority === 'medium';

  const whatsappUrl = generateWhatsAppLink(rfqId, serviceName);
  const phoneUrl = generatePhoneLink();
  const calendarUrl = generateCalendarLink(rfqId, serviceName);

  const handleClick = (channel: ContactChannel, url: string) => {
    trackContactInteraction(rfqId, channel, {
      source: 'inline_buttons',
      priority,
      serviceName: serviceName || '',
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleClick('whatsapp', whatsappUrl)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
          title="WhatsApp"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>WhatsApp</span>
        </button>

        {showPhone && (
          <a
            href={phoneUrl}
            onClick={() => trackContactInteraction(rfqId, 'phone', { source: 'inline_compact', priority })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
            title="Call"
          >
            <Phone className="w-3.5 h-3.5" />
            <span>Call</span>
          </a>
        )}

        {showCalendar && (
          <button
            onClick={() => handleClick('calendar', calendarUrl)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
            title="Book Call"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Book Call</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3">
      {/* WhatsApp - Primary */}
      <button
        onClick={() => handleClick('whatsapp', whatsappUrl)}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-white font-medium text-sm hover:bg-emerald-600 transition-all shadow-sm hover:shadow-md"
      >
        <MessageCircle className="w-4 h-4" />
        <span>WhatsApp</span>
      </button>

      {/* Phone - Secondary (High Priority Only) */}
      {showPhone && (
        <a
          href={phoneUrl}
          onClick={() => trackContactInteraction(rfqId, 'phone', { source: 'inline_buttons', priority })}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-slate-700 font-medium text-sm border border-slate-200 hover:border-green-300 hover:bg-green-50 transition-all shadow-sm"
        >
          <Phone className="w-4 h-4 text-green-600" />
          <span>Call {CONTACT_CONFIG.phone.display}</span>
        </a>
      )}

      {/* Calendar - Secondary */}
      {showCalendar && (
        <button
          onClick={() => handleClick('calendar', calendarUrl)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-slate-700 font-medium text-sm border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm"
        >
          <Calendar className="w-4 h-4 text-blue-600" />
          <span>Schedule Technical Call</span>
        </button>
      )}
    </div>
  );
};

export default FloatingContactWidget;
